/**
 * Server-sent events over fetch (not EventSource), so that every reconnect
 * can send the Last-Event-ID header itself and the backoff is ours:
 * exponential with jitter, reset after a successful connection, honouring
 * the server's `retry:` hint and 503 Retry-After. Events are de-duplicated
 * by their numeric id (the server's monotonic seq). `event: gap` means the
 * server could not replay everything missed: the caller refetches via REST.
 * A silent connection (no heartbeat) is dropped and reopened.
 * `event: poll` (budget shield, 85 %+) means live streaming is paused: the
 * caller refetches via REST, and the client asks again after exactly
 * `poll_seconds`, until the server streams again.
 */

export interface SseMessage {
  id: string | null;
  event: string;
  data: string;
}

export type SseStatus = "connecting" | "open" | "reconnecting" | "polling" | "closed";

export interface SseClientOptions {
  url: string;
  onEvent: (msg: SseMessage) => void;
  /** The server dropped events we missed: reload lists from REST. */
  onGap?: () => void;
  /** Live streaming is paused to save budget: reload lists from REST (called on every poll). */
  onPoll?: (pollSeconds: number) => void;
  onStatus?: (status: SseStatus) => void;
  /** Sent as Last-Event-ID on the first connection ("0" = replay recent history). */
  lastEventId?: string | null;
  fetch?: typeof fetch;
  /** First reconnect delay; the server's `retry:` overrides it. */
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Reconnect when nothing (not even a heartbeat) arrived for this long. */
  idleTimeoutMs?: number;
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/** Incremental parser of the text/event-stream format. */
export class SseParser {
  private buffer = "";
  private data: string[] = [];
  private event = "";
  private id: string | null = null;
  private hasId = false;

  constructor(
    private readonly emit: (msg: SseMessage) => void,
    private readonly onRetry: (ms: number) => void = () => undefined,
  ) {}

  push(chunk: string): void {
    this.buffer += chunk;
    for (;;) {
      const idx = this.buffer.search(/\r\n|\r|\n/);
      if (idx < 0) return;
      const line = this.buffer.slice(0, idx);
      const sep = this.buffer.startsWith("\r\n", idx) ? 2 : 1;
      this.buffer = this.buffer.slice(idx + sep);
      this.line(line);
    }
  }

  private line(line: string): void {
    if (line === "") {
      this.dispatch();
      return;
    }
    if (line.startsWith(":")) return; // comment / heartbeat
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    switch (field) {
      case "data":
        this.data.push(value);
        break;
      case "event":
        this.event = value;
        break;
      case "id":
        if (!value.includes("\0")) {
          this.id = value;
          this.hasId = true;
        }
        break;
      case "retry":
        if (/^\d+$/.test(value)) this.onRetry(Number(value));
        break;
      default:
        break;
    }
  }

  private dispatch(): void {
    const event = this.event || "message";
    const hasPayload = this.data.length > 0 || this.event !== "";
    if (hasPayload) this.emit({ id: this.hasId ? this.id : null, event, data: this.data.join("\n") });
    this.data = [];
    this.event = "";
    this.hasId = false;
  }
}

export class SseClient {
  private readonly opts: Required<Omit<SseClientOptions, "lastEventId" | "onGap" | "onStatus" | "onPoll">> &
    Pick<SseClientOptions, "onGap" | "onStatus" | "onPoll">;
  /** Set while the server says "poll": the next connect waits exactly this long. */
  private pollDelayMs: number | null = null;
  private lastId: string | null;
  private lastSeq = -1;
  private attempt = 0;
  private serverRetryMs: number | null = null;
  private controller: AbortController | null = null;
  private reconnectTimer: unknown = null;
  private idleTimer: unknown = null;
  private stopped = true;

  constructor(options: SseClientOptions) {
    this.opts = {
      fetch: (input, init) => fetch(input, init),
      baseDelayMs: 1000,
      maxDelayMs: 30_000,
      idleTimeoutMs: 45_000,
      random: Math.random,
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (h) => {
        clearTimeout(h as ReturnType<typeof setTimeout>);
      },
      ...options,
    };
    this.lastId = options.lastEventId ?? null;
    if (this.lastId !== null && /^\d+$/.test(this.lastId)) this.lastSeq = Number(this.lastId);
  }

  /** Whether the server's last word was "poll" (changed by handle() while reading). */
  isPolling(): boolean {
    return this.pollDelayMs !== null;
  }

  get lastEventId(): string | null {
    return this.lastId;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.controller?.abort();
    this.controller = null;
    this.clear("reconnectTimer");
    this.clear("idleTimer");
    this.opts.onStatus?.("closed");
  }

  /** Delay before reconnect attempt n (0-based): exponential, capped, with ±50 % jitter. */
  delayFor(attempt: number): number {
    const base = this.serverRetryMs ?? this.opts.baseDelayMs;
    const raw = Math.min(this.opts.maxDelayMs, base * 2 ** attempt);
    return Math.round(raw * (0.5 + this.opts.random() / 2));
  }

  private clear(which: "reconnectTimer" | "idleTimer"): void {
    if (this[which] !== null) this.opts.clearTimer(this[which]);
    this[which] = null;
  }

  private armIdle(): void {
    this.clear("idleTimer");
    this.idleTimer = this.opts.setTimer(() => {
      this.controller?.abort(); // the read loop ends and schedules a reconnect
    }, this.opts.idleTimeoutMs);
  }

  private schedule(delayMs?: number): void {
    if (this.stopped) return;
    this.clear("idleTimer");
    if (this.pollDelayMs !== null) {
      this.opts.onStatus?.("polling");
      const delay = this.pollDelayMs;
      this.reconnectTimer = this.opts.setTimer(() => {
        this.reconnectTimer = null;
        void this.connect();
      }, delay);
      return;
    }
    this.opts.onStatus?.("reconnecting");
    const delay = delayMs ?? this.delayFor(this.attempt);
    this.attempt += 1;
    this.reconnectTimer = this.opts.setTimer(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private handle(msg: SseMessage): void {
    if (msg.id !== null) {
      if (/^\d+$/.test(msg.id)) {
        const seq = Number(msg.id);
        if (seq <= this.lastSeq) return; // duplicate (replay overlap or queue redelivery)
        this.lastSeq = seq;
      }
      this.lastId = msg.id;
    }
    if (msg.event === "gap") {
      this.opts.onGap?.();
      return;
    }
    if (msg.event === "poll") {
      let seconds = 30;
      try {
        const parsed = (JSON.parse(msg.data) as { poll_seconds?: unknown }).poll_seconds;
        if (typeof parsed === "number" && parsed >= 5 && parsed <= 600) seconds = parsed;
      } catch {
        // keep the default
      }
      this.pollDelayMs = seconds * 1000;
      this.opts.onStatus?.("polling");
      this.opts.onPoll?.(seconds);
      return;
    }
    this.opts.onEvent(msg);
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.opts.onStatus?.(this.attempt === 0 ? "connecting" : "reconnecting");
    const controller = new AbortController();
    this.controller = controller;
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (this.lastId !== null) headers["Last-Event-ID"] = this.lastId;

    let res: Response;
    try {
      res = await this.opts.fetch(this.opts.url, { headers, signal: controller.signal, cache: "no-store" });
    } catch {
      this.schedule();
      return;
    }
    if (!res.ok || !res.body) {
      const retryAfter = Number(res.headers.get("Retry-After"));
      void res.body?.cancel().catch(() => undefined);
      if (res.status === 404 || res.status === 400) {
        this.stop(); // the stream itself is gone (e.g. hidden topic): do not hammer it
        return;
      }
      this.schedule(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : undefined);
      return;
    }

    this.attempt = 0;
    const wasPolling = this.pollDelayMs !== null;
    this.pollDelayMs = null; // a poll message on this connection sets it again
    // After a poll period, "open" waits for the first chunk that is not another poll message.
    let announced = !wasPolling;
    if (announced) this.opts.onStatus?.("open");
    this.armIdle();
    const parser = new SseParser(
      (m) => {
        this.handle(m);
      },
      (ms) => {
        this.serverRetryMs = ms;
      },
    );
    const reader = res.body.getReader();
    // Abort (stop, idle watchdog) must also end a pending read.
    controller.signal.addEventListener("abort", () => {
      reader.cancel().catch(() => undefined);
    });
    const decoder = new TextDecoder();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        this.armIdle();
        parser.push(decoder.decode(value, { stream: true }));
        if (!announced && !this.isPolling()) {
          announced = true;
          this.opts.onStatus?.("open");
        }
      }
    } catch {
      // aborted or network drop
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // already released
      }
    }
    if (this.controller === controller) this.controller = null;
    this.schedule();
  }
}
